import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import dynamic from 'next/dynamic';
import type { KeyboardEvent, Ref } from 'react';
import CancelIcon from '@upzi/web-shell/icons/cancel-icon';
import CloseIcon from '@upzi/web-shell/icons/close-icon';
import SearchIcon from '@upzi/web-shell/icons/search-icon';
import { Button } from '@upzi/web-shell/ui/button';
import { Input } from '@upzi/web-shell/ui/input';
import { cn } from '@/lib/utils';
import {
  getJobSearchSuggestionItems,
  JobSearchSuggestions,
} from './job-search-suggestions';

const RECENT_JOB_SEARCHES_STORAGE_KEY = 'upzi:job-searches:v1';
const MAX_RECENT_JOB_SEARCHES = 5;
const loadMobileSearchDialog = () =>
  import('./mobile-job-search-dialog').then(
    module => module.MobileJobSearchDialog
  );
const MobileJobSearchDialog = dynamic(loadMobileSearchDialog, { ssr: false });

export type JobSearchInputHandle = Pick<HTMLInputElement, 'focus'>;

type JobSearchHeroProps = {
  isLoading: boolean;
  isSuggestionsEnabled?: boolean;
  isSuggestionLoading?: boolean;
  onQueryChange: (value: string) => void;
  onSuggestionsActivate?: () => void;
  onSubmit: (query: string) => void;
  query: string;
  queryInputRef?: Ref<JobSearchInputHandle>;
  suggestionKeywords?: string[];
};

function readRecentJobSearches(): string[] {
  try {
    const storedValue = window.localStorage.getItem(
      RECENT_JOB_SEARCHES_STORAGE_KEY
    );
    const parsedValue: unknown = storedValue ? JSON.parse(storedValue) : [];

    if (!Array.isArray(parsedValue)) return [];

    return parsedValue
      .filter((value): value is string => typeof value === 'string')
      .map(value => value.trim())
      .filter(Boolean)
      .slice(0, MAX_RECENT_JOB_SEARCHES);
  } catch {
    return [];
  }
}

function persistRecentJobSearches(searches: string[]) {
  try {
    window.localStorage.setItem(
      RECENT_JOB_SEARCHES_STORAGE_KEY,
      JSON.stringify(searches)
    );
  } catch {
    // Search remains usable when browser storage is unavailable.
  }
}

export function JobSearchHero({
  isLoading,
  isSuggestionsEnabled = true,
  isSuggestionLoading = false,
  onQueryChange,
  onSuggestionsActivate,
  onSubmit,
  query,
  queryInputRef,
  suggestionKeywords = [],
}: JobSearchHeroProps) {
  const inputElementRef = useRef<HTMLInputElement>(null);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const mobileInitialQuery = useRef(query);
  const wasSearching = useRef(false);
  const closeMobileSearch = useCallback(() => {
    setIsMobileSearchOpen(false);
    onQueryChange(mobileInitialQuery.current);
  }, [onQueryChange]);
  const openMobileSearch = () => {
    mobileInitialQuery.current = query;
    onSuggestionsActivate?.();
    setRecentSearches(readRecentJobSearches());
    setIsMobileSearchOpen(true);
  };

  useEffect(() => {
    if (isLoading) {
      wasSearching.current = true;
    } else if (wasSearching.current) {
      wasSearching.current = false;
      setIsMobileSearchOpen(false);
    }
  }, [isLoading]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>(() =>
    typeof window === 'undefined' ? [] : readRecentJobSearches()
  );
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(
    null
  );
  const isSelectedSuggestion = Boolean(
    selectedSuggestion && selectedSuggestion === query
  );
  const hasQuery = query.length > 0;
  const suggestionItems = getJobSearchSuggestionItems(
    query,
    recentSearches,
    suggestionKeywords
  );
  const isDropdownVisible =
    isSuggestionsEnabled &&
    isSuggestionsOpen &&
    !isSelectedSuggestion &&
    (suggestionItems.length > 0 || Boolean(query.trim()));
  const suggestionsId = useId();
  const activeSuggestion =
    activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestionItems.length
      ? suggestionItems[activeSuggestionIndex]
      : undefined;

  useImperativeHandle(
    queryInputRef,
    () => ({
      focus: (options?: FocusOptions) => {
        if (!window.matchMedia('(min-width: 640px)').matches) {
          mobileInitialQuery.current = query;
          onSuggestionsActivate?.();
          setRecentSearches(readRecentJobSearches());
          setIsMobileSearchOpen(true);
        } else {
          inputElementRef.current?.focus(options);
        }
      },
    }),
    [onSuggestionsActivate, query]
  );

  const updateRecentSearches = (nextSearches: string[]) => {
    const boundedSearches = nextSearches.slice(0, MAX_RECENT_JOB_SEARCHES);

    setRecentSearches(boundedSearches);
    persistRecentJobSearches(boundedSearches);
  };

  const addRecentSearch = (value: string) => {
    const normalizedValue = value.trim();

    if (!normalizedValue) return;

    updateRecentSearches([
      normalizedValue,
      ...recentSearches.filter(
        recent =>
          recent.toLocaleLowerCase('vi-VN') !==
          normalizedValue.toLocaleLowerCase('vi-VN')
      ),
    ]);
  };

  const submitQuery = (value: string) => {
    const submittedQuery = value.trim();

    addRecentSearch(submittedQuery);
    setIsSuggestionsOpen(false);
    setActiveSuggestionIndex(-1);
    onSubmit(submittedQuery);
  };

  const handleSubmit = () => submitQuery(query);

  const handleSuggestionSelect = (value: string) => {
    onQueryChange(value);
    setSelectedSuggestion(value);
    submitQuery(value);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsSuggestionsOpen(false);
      setActiveSuggestionIndex(-1);

      return;
    }

    if (
      !isSuggestionsEnabled ||
      isSelectedSuggestion ||
      suggestionItems.length === 0
    ) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsSuggestionsOpen(true);
      setActiveSuggestionIndex(index =>
        index >= suggestionItems.length - 1 ? 0 : index + 1
      );

      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIsSuggestionsOpen(true);
      setActiveSuggestionIndex(index =>
        index <= 0 ? suggestionItems.length - 1 : index - 1
      );

      return;
    }

    if (event.key === 'Enter' && isDropdownVisible && activeSuggestion) {
      event.preventDefault();
      handleSuggestionSelect(activeSuggestion.label);
    }
  };

  return (
    <section className="bg-surface-pure sticky top-0 isolate z-40 h-16 shrink-0 overflow-visible sm:relative sm:h-[72px]">
      <div className="px-4 py-1.5 sm:hidden">
        <div className="border-outline-variant flex h-[52px] min-w-0 items-center rounded-2xl border-2">
          <Button
            type="button"
            variant="ghost"
            aria-label="Tìm kiếm việc làm, công ty"
            onClick={openMobileSearch}
            onPointerDown={() => {
              void loadMobileSearchDialog();
            }}
            className="text-on-surface text-ui-base h-full min-w-0 flex-1 justify-start gap-3 rounded-2xl px-4 font-normal hover:bg-transparent"
          >
            <SearchIcon
              size={24}
              className="text-on-surface-variant size-6 shrink-0"
              aria-hidden
            />
            <span
              className={cn(
                'min-w-0 truncate',
                !hasQuery && 'text-on-surface-variant'
              )}
            >
              {query || 'Tìm kiếm việc làm, công ty...'}
            </span>
          </Button>
          {hasQuery ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Xóa từ khóa tìm kiếm"
              onClick={() => {
                onQueryChange('');
                setSelectedSuggestion(null);
                submitQuery('');
              }}
              className="mr-3 shrink-0 rounded-full"
            >
              <CloseIcon size={20} className="size-5" aria-hidden />
            </Button>
          ) : null}
        </div>
      </div>
      {isMobileSearchOpen ? (
        <MobileJobSearchDialog
          isLoading={isLoading}
          isSuggestionLoading={isSuggestionLoading}
          items={getJobSearchSuggestionItems(
            query,
            recentSearches,
            suggestionKeywords,
            !query.trim() && recentSearches.length === 0 ? 11 : undefined
          )}
          query={query}
          onClose={closeMobileSearch}
          onQueryChange={value => {
            onSuggestionsActivate?.();
            onQueryChange(value);
          }}
          onSubmit={value => {
            onQueryChange(value);
            submitQuery(value);
          }}
          onClearRecent={() => updateRecentSearches([])}
          onRemoveRecent={value =>
            updateRecentSearches(
              recentSearches.filter(recent => recent !== value)
            )
          }
        />
      ) : null}
      <form
        className="relative mx-auto hidden h-full w-full max-w-[1440px] items-start justify-center px-5 pt-3 pb-1 sm:flex sm:px-6 lg:px-20"
        aria-busy={isLoading}
        onSubmit={event => {
          event.preventDefault();
          handleSubmit();
        }}
        onBlur={event => {
          if (
            !event.currentTarget.contains(event.relatedTarget as Node | null)
          ) {
            setIsSuggestionsOpen(false);
            setActiveSuggestionIndex(-1);
          }
        }}
      >
        <div
          className={cn(
            'relative z-50 flex w-full max-w-[594px] min-w-0 flex-col overflow-hidden p-1 transition-[background-color,box-shadow,border-radius] duration-200 ease-out motion-reduce:transition-none',
            isDropdownVisible
              ? 'bg-surface-pure shadow-search-suggestions ring-search-suggestions-outline rounded-[32px] ring-1 ring-inset'
              : 'h-[60px]'
          )}
        >
          <div
            className={cn(
              'flex h-[52px] w-full shrink-0 items-center transition-[padding] duration-200 ease-out motion-reduce:transition-none',
              isDropdownVisible ? 'px-1 pt-1' : 'p-0.5'
            )}
          >
            <div
              className={cn(
                'group/search-input border-on-surface bg-surface-pure hover:bg-surface-variant focus-within:!bg-surface-pure relative flex h-12 w-full min-w-0 items-center overflow-hidden rounded-3xl border transition-colors focus-within:border-2',
                isDropdownVisible && 'gap-2 pr-0.5'
              )}
            >
              <Input
                ref={inputElementRef}
                role="combobox"
                aria-autocomplete="list"
                aria-controls={isDropdownVisible ? suggestionsId : undefined}
                aria-expanded={isDropdownVisible}
                aria-activedescendant={
                  isDropdownVisible && activeSuggestion
                    ? `${suggestionsId}-${activeSuggestion.id}`
                    : undefined
                }
                aria-label="Tìm kiếm việc làm tại địa điểm, công ty, kỹ năng"
                value={query}
                onChange={event => {
                  onSuggestionsActivate?.();
                  onQueryChange(event.target.value);
                  setSelectedSuggestion(null);
                  setIsSuggestionsOpen(
                    isSuggestionsEnabled || Boolean(onSuggestionsActivate)
                  );
                  setActiveSuggestionIndex(-1);
                }}
                onFocus={() => {
                  onSuggestionsActivate?.();
                  if (
                    (isSuggestionsEnabled || onSuggestionsActivate) &&
                    !isSelectedSuggestion
                  ) {
                    setIsSuggestionsOpen(true);
                  }
                }}
                onKeyDown={handleInputKeyDown}
                placeholder="Tìm kiếm việc làm tại địa điểm, công ty, kỹ năng"
                icon={
                  <SearchIcon
                    className="text-on-surface size-5"
                    size={20}
                    aria-hidden
                  />
                }
                classNameWrapper="h-full min-w-0 flex-1"
                className="text-on-surface group-hover/search-input:text-base-muted-foreground group-focus-within/search-input:!text-on-surface text-ui-base placeholder:text-base-muted-foreground font-body md:text-ui-base h-full rounded-none border-0 bg-transparent py-0 pr-2 pl-10 leading-5 font-normal transition-colors placeholder:text-base placeholder:leading-5 placeholder:font-normal placeholder:opacity-100 focus-visible:border-0 focus-visible:ring-0"
              />
              {hasQuery ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Xóa từ khóa tìm kiếm"
                  onClick={() => {
                    onQueryChange('');
                    setSelectedSuggestion(null);
                    setIsSuggestionsOpen(isSuggestionsEnabled);
                    setActiveSuggestionIndex(-1);
                    inputElementRef.current?.focus();
                  }}
                  className={cn(
                    'text-on-surface hover:text-on-surface size-9 shrink-0 rounded-full bg-transparent p-0 shadow-none hover:bg-transparent',
                    !isDropdownVisible && 'mr-3'
                  )}
                >
                  <CancelIcon
                    className="size-5"
                    size={20}
                    variant="outline"
                    aria-hidden
                  />
                </Button>
              ) : null}

              {isDropdownVisible ? (
                <Button
                  type="submit"
                  className="bg-primary text-on-primary hover:bg-primary h-10 w-[89px] shrink-0 rounded-[26px] px-3 py-2 text-base leading-5 font-normal shadow-none"
                >
                  Tìm kiếm
                </Button>
              ) : null}
            </div>
          </div>

          {isDropdownVisible ? (
            <JobSearchSuggestions
              id={suggestionsId}
              activeIndex={activeSuggestionIndex}
              items={suggestionItems}
              isLoading={isSuggestionLoading}
              query={query}
              onSelect={handleSuggestionSelect}
              onClearRecent={() => updateRecentSearches([])}
              onRemoveRecent={value =>
                updateRecentSearches(
                  recentSearches.filter(recent => recent !== value)
                )
              }
            />
          ) : null}
        </div>
      </form>
    </section>
  );
}
